import React, { useState, useEffect, useRef } from 'react';
import CONFIG from '../config';

const VIEWBOX_WIDTH = 1000;
const VIEWBOX_HEIGHT = 300;
const MARGIN = { top: 30, right: 30, bottom: 20, left: 30 };
const MIN_SEPARACION_PX = 40;
const FADE_MS = 400;
const OFFSET_ARRIBA = -24;
const OFFSET_ABAJO = 24;

function calcularMarcadoresRaw(perfilElevacion, primeros, xFor, yFor) {
  const totalKm = perfilElevacion[perfilElevacion.length - 1].distanciaKm || 0;
  return (primeros || []).map((r) => ({
    dorsal: r.dorsal,
    position: r.position,
    x: xFor((r.porcentaje / 100) * totalKm),
    y: yFor(r.elevacion)
  }));
}

function AltimetriaChart({ perfilElevacion, primeros }) {
  const [displayed, setDisplayed] = useState([]);
  const timeoutsRef = useRef({});
  const rafIdsRef = useRef([]);
  const offsetsRef = useRef({});
  const colorsRef = useRef({});
  // Orden de pintado (z) persistente por dorsal, asignado UNA VEZ al
  // entrar en pantalla - nunca se recalcula por ranking. Antes el
  // orden de pintado seguia al ranking (1o/2o/3o), que es justo lo que
  // cambia en el instante del adelantamiento -> por eso "saltaba" quien
  // quedaba encima justo al pasar. Con esto, quien esta encima/debajo
  // se queda fijo mientras el corredor siga en pantalla.
  const zOrderRef = useRef({});
  const zCounterRef = useRef(0);

  const tieneDatos = perfilElevacion && perfilElevacion.length >= 2;

  const innerWidth = VIEWBOX_WIDTH - MARGIN.left - MARGIN.right;
  const innerHeight = VIEWBOX_HEIGHT - MARGIN.top - MARGIN.bottom;
  const totalKm = tieneDatos ? perfilElevacion[perfilElevacion.length - 1].distanciaKm : 0;
  const elevaciones = tieneDatos ? perfilElevacion.map(p => p.elevacion) : [0];
  const elevMin = Math.min(...elevaciones);
  const elevMax = Math.max(...elevaciones);
  const elevRango = (elevMax - elevMin) || 1;

  const xFor = (km) => MARGIN.left + (km / (totalKm || 1)) * innerWidth;
  const yFor = (elevacion) => MARGIN.top + innerHeight - ((elevacion - elevMin) / elevRango) * innerHeight;

  useEffect(() => {
    if (!tieneDatos) return;

    const nuevosRaw = calcularMarcadoresRaw(perfilElevacion, primeros, xFor, yFor);
    const nuevosDorsales = new Set(nuevosRaw.map(m => m.dorsal));

    setDisplayed(prevDisplayed => {
      const prevByDorsal = {};
      prevDisplayed.forEach(m => { prevByDorsal[m.dorsal] = m; });

      nuevosRaw.forEach(m => {
        if (colorsRef.current[m.dorsal] === undefined) {
          const usados = new Set(Object.values(colorsRef.current));
          let colorElegido = CONFIG.COLORES_FAVORITOS.find(c => !usados.has(c));
          if (!colorElegido) colorElegido = CONFIG.COLORES_FAVORITOS[0];
          colorsRef.current[m.dorsal] = colorElegido;
        }
        if (zOrderRef.current[m.dorsal] === undefined) {
          zOrderRef.current[m.dorsal] = zCounterRef.current++;
        }
      });

      const asignadosParaConflicto = [];
      [...nuevosRaw].sort((a, b) => a.x - b.x).forEach(m => {
        let offset = offsetsRef.current[m.dorsal];
        if (offset === undefined) {
          const conflicto = asignadosParaConflicto.find(
            a => Math.abs(a.x - m.x) < MIN_SEPARACION_PX
          );
          offset = (conflicto && conflicto.offset === OFFSET_ARRIBA) ? OFFSET_ABAJO : OFFSET_ARRIBA;
          offsetsRef.current[m.dorsal] = offset;
        }
        asignadosParaConflicto.push({ x: m.x, offset });
      });

      const ordenadosPorX = [...nuevosRaw].sort((a, b) => a.x - b.x);
      for (let i = 1; i < ordenadosPorX.length; i++) {
        const anterior = ordenadosPorX[i - 1];
        const actual = ordenadosPorX[i];
        const offsetAnterior = offsetsRef.current[anterior.dorsal];
        const offsetActual = offsetsRef.current[actual.dorsal];
        if (Math.abs(actual.x - anterior.x) < MIN_SEPARACION_PX && offsetAnterior === offsetActual) {
          offsetsRef.current[actual.dorsal] = (offsetActual === OFFSET_ARRIBA) ? OFFSET_ABAJO : OFFSET_ARRIBA;
        }
      }

      const actualizados = nuevosRaw.map(m => {
        const esNuevo = !prevByDorsal[m.dorsal];
        if (timeoutsRef.current[m.dorsal]) {
          clearTimeout(timeoutsRef.current[m.dorsal]);
          delete timeoutsRef.current[m.dorsal];
        }
        return {
          ...m,
          color: colorsRef.current[m.dorsal],
          labelOffset: offsetsRef.current[m.dorsal],
          entering: esNuevo,
          exiting: false
        };
      });

      const saliendoNuevos = prevDisplayed.filter(
        m => !nuevosDorsales.has(m.dorsal) && !m.exiting
      );
      saliendoNuevos.forEach(m => {
        timeoutsRef.current[m.dorsal] = setTimeout(() => {
          setDisplayed(current => current.filter(x => x.dorsal !== m.dorsal));
          delete timeoutsRef.current[m.dorsal];
          delete offsetsRef.current[m.dorsal];
          delete colorsRef.current[m.dorsal];
          delete zOrderRef.current[m.dorsal];
        }, FADE_MS);
      });

      const yaSaliendo = prevDisplayed.filter(
        m => !nuevosDorsales.has(m.dorsal) && m.exiting
      );

      const entrandoAhora = actualizados.filter(m => m.entering);
      if (entrandoAhora.length) {
        const id1 = requestAnimationFrame(() => {
          const id2 = requestAnimationFrame(() => {
            setDisplayed(current => current.map(x =>
              entrandoAhora.some(e => e.dorsal === x.dorsal) ? { ...x, entering: false } : x
            ));
          });
          rafIdsRef.current.push(id2);
        });
        rafIdsRef.current.push(id1);
      }

      return [
        ...actualizados,
        ...saliendoNuevos.map(m => ({ ...m, exiting: true })),
        ...yaSaliendo
      ];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilElevacion, primeros]);

  useEffect(() => {
    return () => {
      Object.values(timeoutsRef.current).forEach(clearTimeout);
      rafIdsRef.current.forEach(cancelAnimationFrame);
    };
  }, []);

  if (!tieneDatos) return null;

  const puntosLinea = perfilElevacion.map(p => `${xFor(p.distanciaKm).toFixed(1)},${yFor(p.elevacion).toFixed(1)}`);
  const lineaPath = `M ${puntosLinea.join(' L ')}`;
  const baseY = MARGIN.top + innerHeight;
  const areaPath = `${lineaPath} L ${xFor(totalKm).toFixed(1)},${baseY} L ${xFor(0).toFixed(1)},${baseY} Z`;

  // Orden de pintado real: por zOrder persistente, NUNCA por ranking.
  const paraPintar = [...displayed].sort(
    (a, b) => (zOrderRef.current[a.dorsal] ?? 0) - (zOrderRef.current[b.dorsal] ?? 0)
  );

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', display: 'block', background: '#000000' }}
    >
      <path d={areaPath} fill="#1C272B" stroke="none" />
      <path d={lineaPath} fill="none" stroke="#ffffff" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
      <line x1={MARGIN.left} y1={baseY} x2={VIEWBOX_WIDTH - MARGIN.right} y2={baseY} stroke="#555555" strokeWidth="1" />

      {paraPintar.map((m) => {
        const opacity = (m.exiting || m.entering) ? 0 : 1;
        return (
          <g
            key={m.dorsal}
            style={{
              opacity,
              transform: `translate(${m.x}px, ${m.y}px)`,
              transition: `opacity ${FADE_MS}ms ease, transform 0.24s linear`
            }}
          >
            <line x1={0} y1={0} x2={0} y2={m.labelOffset} stroke={m.color} strokeWidth="2" />
            <circle cx={0} cy={0} r="8" fill={m.color} stroke="#ffffff" strokeWidth="2" />
            <text
              x={0}
              y={m.labelOffset + (m.labelOffset > 0 ? 18 : -6)}
              textAnchor="middle"
              fontSize="20"
              fontWeight="700"
              fill="#ffffff"
              fontFamily="Montserrat, sans-serif"
            >
              {m.dorsal}
            </text>
            <text
              x={0}
              y={m.labelOffset + (m.labelOffset > 0 ? 34 : -30)}
              textAnchor="middle"
              fontSize="13"
              fontWeight="600"
              fill={m.color}
              fontFamily="Montserrat, sans-serif"
            >
              {m.position}º
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default AltimetriaChart;
